import React, { useState, useCallback, useRef } from 'react';
import {
  IconButton,
  Tooltip,
  Chip,
  Box,
  Stack
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  RadioButtonChecked as RecordingIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { analytics } from '../utils/analytics';

// Animation for recording state
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

/**
 * Intuitive Voice Input Component
 * Click mic → starts recording immediately → shows feedback → auto-stops → puts text in field
 */
const InlineVoiceInput = ({ 
  onTranscript, 
  onListeningChange,
  onRecordingStateChange,
  onPermissionError, // Callback when mic permission is denied
  onSpeechStart, // 🆕 NEW: Callback when user starts speaking (for audio interruption)
  size = 'medium',
  disabled = false,
  silenceTimeout = 5000, // Milliseconds before auto-stop (default 5s)
  autoStart = false, // If true, automatically starts listening when component mounts/updates
  analyticsSource = 'unknown'
}) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [audioLevels, setAudioLevels] = useState([6, 6, 6, 6, 6, 6, 6, 6]); // Default minimum height
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const timeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const streamRef = useRef(null);

  const isSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  /**
   * Start REAL audio level monitoring from microphone
   */
  const startAudioMonitoring = useCallback(async () => {
    // console.log('🎤 Starting REAL audio monitoring...');
    
    try {
      // Get microphone access with optimal settings for voice
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 44100
        }
      });
      
      streamRef.current = stream;
      setMicPermissionDenied(false); // Reset permission state on success
    //   console.log('✅ Microphone access granted for audio monitoring');
      
      // Create Web Audio API context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      
      // Configure analyser for responsive audio visualization
      analyserRef.current.fftSize = 512; // Higher resolution for better frequency analysis
      analyserRef.current.smoothingTimeConstant = 0.2; // Quick response to audio changes
      analyserRef.current.minDecibels = -90;
      analyserRef.current.maxDecibels = -10;
      
      // Connect microphone to analyser
      microphoneRef.current.connect(analyserRef.current);
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
    //   console.log('🔊 Audio pipeline connected, buffer length:', bufferLength);
      
      let isActive = true;
      
      const updateAudioLevels = () => {
        if (!isActive || !analyserRef.current) {
          console.log('🛑 Real audio monitoring stopped');
          return;
        }
        
        // Get real-time frequency data from microphone
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate 8 bars with voice-optimized frequency distribution
        const levels = [];
        const barsCount = 8;
        let totalActivity = 0; // Track overall audio activity for silence detection
        
        // Voice frequencies are typically 85Hz - 8kHz, so we focus on lower frequencies
        // and use different strategies for each bar to ensure all bars are active
        
        for (let i = 0; i < barsCount; i++) {
          let sum = 0;
          let count = 0;
          
          if (i < 4) {
            // Bars 0-3: Use actual frequency ranges (bass to mid frequencies)
            const frequencyRangeSize = Math.floor(bufferLength / 2 / 4); // Use first half of spectrum
            const startFreq = i * frequencyRangeSize;
            const endFreq = Math.min(startFreq + frequencyRangeSize, bufferLength / 2);
            
            for (let j = startFreq; j < endFreq; j++) {
              sum += dataArray[j];
              count++;
            }
          } else {
            // Bars 4-7: Use overlapping ranges with different weightings for more activity
            const baseRange = Math.floor(bufferLength / 4); // Use first quarter for high-freq bars too
            const startFreq = (i - 4) * Math.floor(baseRange / 4);
            const endFreq = Math.min(startFreq + Math.floor(baseRange / 2), bufferLength / 4);
            
            for (let j = startFreq; j < endFreq; j++) {
              // Add some frequency data plus some time-based variation for more liveliness
              const timeVariation = Math.sin(Date.now() * 0.01 + i) * 20;
              sum += dataArray[j] + Math.max(0, timeVariation);
              count++;
            }
          }
          
          if (count === 0) count = 1; // Prevent division by zero
          
          // Calculate average amplitude for this range
          const average = sum / count;
          totalActivity += average; // Add to total activity for silence detection
          
          // Convert to visual height with enhanced sensitivity for voice
          const normalizedLevel = Math.min(average / 128, 1); // More sensitive scaling
          const height = Math.max(6, Math.min(35, 6 + normalizedLevel * 29));
          
          levels.push(Math.round(height));
        }
        
        // Silence detection: Check if user stopped talking
        // NOTE: We don't auto-stop on silence anymore because:
        // 1. In talk mode, ChatInterface handles auto-send after silence
        // 2. Stopping recognition clears the transcript prematurely
        // 3. The recognition will be manually stopped after sending or by user
        const averageActivity = totalActivity / barsCount;
        const silenceThreshold = 20; // Adjust this value as needed (lower = more sensitive)
        
        // Just track silence for visual feedback, don't auto-stop
        // if (averageActivity < silenceThreshold) {
        //   // User is not talking - start or continue silence timer
        //   if (!silenceTimeoutRef.current) {
        //     silenceTimeoutRef.current = setTimeout(() => {
        //       console.log('🔇 Silence detected - auto-stopping recording');
        //       if (recognition) {
        //         recognition.stop();
        //       }
        //     }, silenceTimeout); // Use configurable silence timeout
        //   }
        // } else {
        //   // User is talking - clear silence timer
        //   if (silenceTimeoutRef.current) {
        //     clearTimeout(silenceTimeoutRef.current);
        //     silenceTimeoutRef.current = null;
        //   }
        // }
        
        // // Only log occasionally to avoid console spam
        // if (Math.random() < 0.05) { // 5% chance
        //   console.log('🎵 REAL audio levels:', levels, 'avg:', Math.round(levels.reduce((a,b) => a+b) / levels.length));
        // }
        
        setAudioLevels(levels);
        
        if (isActive) {
          animationRef.current = requestAnimationFrame(updateAudioLevels);
        }
      };
      
      // Store stop function for cleanup
      animationRef.stopAnimation = () => {
        isActive = false;
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
      };
      
      // Start the real audio monitoring loop
      updateAudioLevels();
      
    } catch (error) {
      console.error('🚫 Failed to access microphone for real audio monitoring:', error);
      
      // Check if it's a permission error
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        console.error('🚫 Microphone permission denied!');
        setMicPermissionDenied(true);
        if (onPermissionError) {
          onPermissionError('Microphone access denied. Please enable microphone permissions in your browser settings.');
        }
        return; // Don't fall back to simulated animation on permission error
      }
      
      console.log('📱 Falling back to simulated animation...');
      
      // Fallback to simulated animation if microphone access fails (but not permission issues)
      let startTime = Date.now();
      let isActive = true;
      
      const updateSimulatedLevels = () => {
        if (!isActive) return;
        
        const elapsed = (Date.now() - startTime) / 1000;
        const levels = [];
        
        for (let i = 0; i < 8; i++) {
          const freq1 = 0.8 + i * 0.2;
          const freq2 = 1.2 + i * 0.15;
          const phase = i * Math.PI / 3;
          
          const wave1 = Math.sin(elapsed * freq1 * 2 * Math.PI + phase);
          const wave2 = Math.sin(elapsed * freq2 * 2 * Math.PI + phase + Math.PI/4) * 0.6;
          const noise = (Math.random() - 0.5) * 0.3;
          
          const combined = (wave1 + wave2 + noise) * 0.5 + 0.5;
          const height = Math.max(6, Math.min(35, 6 + combined * 29));
          
          levels.push(Math.round(height));
        }
        
        setAudioLevels(levels);
        
        if (isActive) {
          animationRef.current = requestAnimationFrame(updateSimulatedLevels);
        }
      };
      
      animationRef.stopAnimation = () => {
        isActive = false;
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
      };
      
      updateSimulatedLevels();
    }
  }, []);

  /**
   * Stop audio level monitoring and cleanup all resources
   */
  const stopAudioMonitoring = useCallback(() => {
    // console.log('🛑 Stopping audio monitoring...');
    
    // Stop the animation loop
    if (animationRef.stopAnimation) {
      animationRef.stopAnimation();
    }
    
    // Clear silence timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    
    // Disconnect audio nodes
    if (microphoneRef.current) {
      microphoneRef.current.disconnect();
      microphoneRef.current = null;
    }
    
    // Stop microphone stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🔇 Microphone track stopped');
      });
      streamRef.current = null;
    }
    
    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    analyserRef.current = null;
    setAudioLevels([6, 6, 6, 6, 6, 6, 6, 6]); // Reset to minimum height (8 bars)
  }, []);

  const startListening = useCallback(() => {
    console.log('🎬 startListening() called', { isSupported, disabled, isListening });
    
    if (!isSupported || disabled) {
      console.log('❌ Cannot start: not supported or disabled');
      return;
    }

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      console.log('🎙️ Creating new recognition instance...');
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      let finalTranscript = '';
      let speechStarted = false; // Track if we've called onSpeechStart

      recognitionInstance.onstart = () => {
        console.log('✅ Speech recognition STARTED successfully');
        setIsListening(true);
        analytics.voiceInputStart({
          surface: analyticsSource,
          trigger: autoStart ? 'auto' : 'manual',
        });
        if (onListeningChange) onListeningChange(true);
        if (onRecordingStateChange) onRecordingStateChange(true);
        
        // Set recognition in state so it's accessible to audio monitoring
        setRecognition(recognitionInstance);
        
        // Start audio monitoring immediately
        startAudioMonitoring();
        
        // Auto-stop after 30 seconds
        timeoutRef.current = setTimeout(() => {
        //   console.log('⏰ Auto-stopping due to timeout');
          recognitionInstance.stop();
        }, 30000);
      };

      recognitionInstance.onresult = (event) => {
        let interimTranscript = '';
        let newFinalTranscript = '';
        let hasFinal = false;
        
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            newFinalTranscript += transcript;
            hasFinal = true;
            console.log('✅ Web Speech API detected FINAL result:', transcript);
          } else {
            interimTranscript += transcript;
            console.log('🔄 Web Speech API detected INTERIM result:', transcript);
          }
        }
        
        // 🆕 NEW: Call speech start callback on first detected speech
        const fullText = finalTranscript + newFinalTranscript + interimTranscript;
        if (!speechStarted && fullText.trim() && onSpeechStart) {
          speechStarted = true;
          console.log('🗣️ === SPEECH START DETECTED ===');
          console.log('🗣️ Text detected:', fullText.substring(0, 50));
          console.log('🗣️ Calling onSpeechStart callback...');
          onSpeechStart(fullText.trim());
          console.log('🗣️ === SPEECH START CALLBACK COMPLETE ===');
        } else if (!speechStarted && fullText.trim()) {
          console.log('⚠️ Speech detected but no onSpeechStart callback provided');
        }
        
        // Update the accumulated final transcript
        if (hasFinal) {
          finalTranscript += newFinalTranscript;
        }
        
        // Send transcript with appropriate final flag
        const currentText = finalTranscript + interimTranscript;
        if (onTranscript && currentText.trim()) {
          console.log('📝 Sending transcript to ChatInterface:', { currentText, isFinal: hasFinal });
          onTranscript(currentText, hasFinal); // Send with correct isFinal flag
        }
      };

      recognitionInstance.onend = () => {
        console.log('🔚 Recognition ended');
        setIsListening(false);
        analytics.voiceInputStop({
          surface: analyticsSource,
          reason: 'session_end',
        });
        if (onListeningChange) onListeningChange(false);
        if (onRecordingStateChange) onRecordingStateChange(false);
        
        // Stop audio monitoring
        stopAudioMonitoring();
        
        // Clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        // Clear silence timeout
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        
        // Send final transcript
        if (finalTranscript.trim() && onTranscript) {
          onTranscript(finalTranscript.trim(), true); // true = final
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('❌ Speech recognition ERROR:', event.error, event);
        analytics.voiceInputError({
          surface: analyticsSource,
          error: event.error || 'recognition_error',
        });
        
        // Handle microphone permission denied
        if (event.error === 'not-allowed' || event.error === 'permission-denied') {
          console.error('🚫 Microphone permission denied!');
          setMicPermissionDenied(true);
          if (onPermissionError) {
            onPermissionError('Microphone access denied. Please enable microphone permissions in your browser settings.');
          }
        }
        
        setIsListening(false);
        if (onListeningChange) onListeningChange(false);
        if (onRecordingStateChange) onRecordingStateChange(false);
        
        // Stop audio monitoring
        stopAudioMonitoring();
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        // Clear silence timeout
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
      };

      // Just start recognition, audio monitoring will start in onstart callback
      console.log('🚀 Calling recognition.start()...');
      recognitionInstance.start();
      
    } catch (err) {
      console.error('🚫 Failed to start voice recognition:', err);
      analytics.voiceInputError({
        surface: analyticsSource,
        error: 'start_failed',
      });
      if (onPermissionError) {
        onPermissionError('Failed to start voice recognition. Please check your browser settings.');
      }
    }
  }, [
    isSupported,
    disabled,
    onTranscript,
    onListeningChange,
    onPermissionError,
    onRecordingStateChange,
    onSpeechStart,
    startAudioMonitoring,
    stopAudioMonitoring,
    autoStart,
    analyticsSource,
  ]);

  const stopListening = useCallback(() => {
    if (recognition) {
      recognition.stop();
    }
  }, [recognition]);

  const handleClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Handle autoStart prop - start listening when requested
  React.useEffect(() => {
    console.log('🎙️ === INLINE VOICE INPUT: autoStart useEffect triggered ===');
    console.log('🎙️ Current state:', {
      autoStart,
      isListening,
      disabled,
      hasRecognition: !!recognition,
      timestamp: new Date().toLocaleTimeString()
    });
    
    if (autoStart && !disabled && !isListening) {
      console.log('✅ CONDITIONS MET - Calling startListening() because autoStart=true and not listening...');
      console.log('▶️ About to start voice recognition...');
      startListening();
    } else if (!autoStart && isListening) {
      console.log('🛑 Stopping listening because autoStart=false...');
      console.log('⏹️ Calling stopListening() because autoStart=false...');
      stopListening();
    } else {
      console.log('⏸️ No action - autoStart:', autoStart, 'isListening:', isListening, 'disabled:', disabled);
    }
  }, [autoStart, disabled, startListening, stopListening]); // Only depend on autoStart and disabled

  if (!isSupported) {
    return (
      <Tooltip title="Voice input not supported in this browser">
        <span>
          <IconButton disabled size={size}>
            <MicOffIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const getIcon = () => {
    if (isListening) {
      return <StopIcon />;
    }
    return <MicIcon />;
  };

  const getTooltip = () => {
    if (disabled) return 'Voice input disabled';
    if (isListening) return 'Recording... Click to stop';
    return 'Click to start voice input';
  };

  // Get responsive dimensions based on size prop
  const getDimensions = () => {
    switch (size) {
      case 'small':
        return {
          buttonSize: 40,
          iconSize: 'medium',
          barsHeight: 36,
          barWidth: '4px',
          minWidth: 80,
          maxBarHeight: 28,
          minBarHeight: 4,
          padding: '6px'
        };
      case 'medium':
        return {
          buttonSize: 49,
          iconSize: 'medium',
          barsHeight: 43,
          barWidth: '5px',
          minWidth: 90,
          maxBarHeight: 37,
          minBarHeight: 4,
          padding: '8px'
        };
      case 'large':
        return {
          buttonSize: 48,
          iconSize: 'large',
          barsHeight: 40,
          barWidth: '6px',
          minWidth: 110,
          maxBarHeight: 32,
          minBarHeight: 5,
          padding: '8px'
        };
      default:
        return getDimensions.call(this, 'medium');
    }
  };

  const dims = getDimensions();

  /**
   * Sound bars component for recording visualization
   */
  const SoundBars = () => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '3px',
      height: `${dims.barsHeight}px`,
      minWidth: `${dims.minWidth}px`
    }}>
      {audioLevels.map((height, index) => (
        <Box
          key={index}
          sx={{
            width: dims.barWidth,
            backgroundColor: 'primary.main',
            borderRadius: '2px',
            height: `${Math.min(Math.max(height * 0.8, dims.minBarHeight), dims.maxBarHeight)}px`,
            transition: 'height 0.1s ease-out',
            minHeight: `${dims.minBarHeight}px`
          }}
        />
      ))}
    </Box>
  );

  if (isListening) {
    // When recording, show bars and stop button in a wider container, pushed to right
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-end',
        width: '100%',
        minWidth: dims.minWidth + dims.buttonSize + 8
      }}>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <SoundBars />
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            size={size}
            color="error"
            sx={{
              animation: `${pulse} 1.5s infinite`,
              width: `${dims.buttonSize}px`,
              height: `${dims.buttonSize}px`,
              padding: dims.padding,
              ml: 0.25
            }}
          >
            <StopIcon fontSize={dims.iconSize} />
          </IconButton>
        </Stack>
      </Box>
    );
  }

  return (
    <IconButton
      onClick={handleClick}
      disabled={disabled}
      size={size}
      color="primary"
      sx={{
        width: `${dims.buttonSize}px`,
        height: `${dims.buttonSize}px`,
        padding: dims.padding
      }}
    >
      <MicIcon fontSize={dims.iconSize} />
    </IconButton>
  );
};

export default InlineVoiceInput;
